/**
 * Public-side facility booking API client. Mirrors publicCampsAPI's
 * shape (uses the public axios instance, no auth header), and reuses
 * lookupMemberByEmail / verifyMemberByEmail from publicCampsAPI since
 * those endpoints are tenant-scoped, not camp-specific — re-exported
 * here so consumer pages have a single import.
 */

import axios from "axios";
import config from "./config";
import { lookupMemberByEmail, verifyMemberByEmail } from "./publicCampsAPI";

const BASE_URL = config.BASE_URL;

const headers = (tenantId: string) => ({
  "Content-Type": "application/json",
  "x-tenant-id": tenantId,
});

export interface PublicResource {
  id: string;
  name: string;
  description?: string | null;
  capacity: number;
  visibility: "public";
  default_approval_mode?: "auto" | "staff_review";
  metadata?: Record<string, any>;
  image_urls?: Array<{ url: string; alt?: string }>;
  facility_resource_types?: { display_name: string; is_quantity_tracked: boolean };
}

export interface PublicService {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  duration_minutes: number;
  approval_mode: "auto" | "staff_review";
  metadata?: Record<string, any>;
  image_urls?: Array<{ url: string; alt?: string }>;
  facility_service_components?: any[];
}

export interface PublicAvailabilityResponse {
  date: string;
  timezone: string;
  duration_min?: number;
  increments_min?: number;
  capacity?: number;
  is_quantity_tracked?: boolean;
  slots: Array<{ start_at: string; end_at: string }>;
  debug?: Record<string, any>;
}

interface ApiOk<T> { success: true; data: T; }
interface ApiErr { success: false; error?: string; message?: string; }
type ApiResponse<T> = ApiOk<T> | ApiErr;

async function pubGet<T>(tenantId: string, path: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
  try {
    const url = new URL(`${BASE_URL}/public/facility/${path}`);
    if (params) for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
    const res = await axios.get(url.toString(), { headers: headers(tenantId) });
    return res.data;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.response?.data?.message || err?.message || "Request failed" };
  }
}
async function pubPost<T>(tenantId: string, path: string, body: any): Promise<ApiResponse<T>> {
  try {
    const res = await axios.post(`${BASE_URL}/public/facility/${path}`, body, { headers: headers(tenantId) });
    return res.data;
  } catch (err: any) {
    // Keep the server's own body (code, requires_password_verification,
    // next_slots): it resolves the BODY on success, so it fails flat with the
    // body's fields. Dropping them left the page's handling of each dead.
    const data = err?.response?.data;
    const kept = data && typeof data === "object" ? data : {};
    return { ...kept, success: false, error: data?.error || data?.message || err?.message || "Request failed" };
  }
}

export const listPublicResources = (tenantId: string) => pubGet<PublicResource[]>(tenantId, "resources");
export const listPublicServices = (tenantId: string) => pubGet<PublicService[]>(tenantId, "services");

export const getPublicResourceAvailability = (tenantId: string, resourceId: string, date: string) =>
  pubGet<PublicAvailabilityResponse>(tenantId, `resources/${resourceId}/availability`, { date });

export const getPublicServiceAvailability = (tenantId: string, serviceId: string, date: string) =>
  pubGet<PublicAvailabilityResponse>(tenantId, `services/${serviceId}/availability`, { date });

export interface PublicBookingRequirementsResponse {
  required_waivers: Array<{ id: string; title: string; content: string }>;
}
export const getPublicResourceRequirements = (tenantId: string, resourceId: string) =>
  pubGet<PublicBookingRequirementsResponse>(tenantId, `resources/${resourceId}/requirements`);
export const getPublicServiceRequirements = (tenantId: string, serviceId: string) =>
  pubGet<PublicBookingRequirementsResponse>(tenantId, `services/${serviceId}/requirements`);

export interface CreatePublicBookingBody {
  resource_id?: string;
  service_id?: string;
  start_at: string;
  end_at: string;
  party_size?: number;
  contact_email: string;
  contact_name?: string;
  contact_phone?: string;
  membership_id?: string | null;
  /** The proof from verify-member that this visitor verified as that member. Required whenever membership_id is sent. */
  member_token?: string | null;
  paid_in_full_at_submit?: boolean;
  notes?: string;
  waiver_acknowledgments?: string[];
}

export interface CreatePublicBookingResponse {
  success: true;
  data: any;
  clientSecret?: string;
  intent_mode?: "payment" | "setup";
  requires_approval?: boolean;
  setup_intent_id?: string;
  summary?: { total: number; deposit_due: number; balance_due_at_event: number };
}

export const createPublicBooking = (tenantId: string, body: CreatePublicBookingBody) =>
  pubPost<any>(tenantId, "bookings", body);

// Re-export tenant-scoped lookup/verify for convenience.
export { lookupMemberByEmail, verifyMemberByEmail };
